import { format } from 'date-fns';
import { format as formatTz, utcToZonedTime } from 'date-fns-tz';

export type DateFormatterOptions = {
	ticks: number;
	min: number;
	max: number;
	userTimeZone?: null | string;
};

// Number of milliseconds in a year
const YEAR = 1000 * 60 * 60 * 24 * 365;

// Number of milliseconds in a month
const MONTH = 1000 * 60 * 60 * 24 * 30;

// Number of milliseconds in a day
const DAY = 1000 * 60 * 60 * 24;

export type DateFormatterFunction = (timestamp: number) => string;

/**
 * Based on a certain time range and a number of ticks, generates a `DateFormatterFunction` for
 * formatting dates.
 */
const makeDateFormatter = (options: DateFormatterOptions): DateFormatterFunction => {
	const dateFormat = (() => {
		// Get the actually visible boundaries
		// Time difference that is in the range
		const diff = options.max - options.min;
		// Time difference between each tick of the time axis
		const ticksDiff = diff / options.ticks;

		if (ticksDiff > YEAR) {
			return 'yyyy';
		}
		if (ticksDiff > MONTH) {
			return 'MMM yyyy';
		}
		if (ticksDiff > DAY) {
			return 'MMM dd';
		}

		return 'hh:mm dd MMM';
	})();

	return (timestamp: number) => {
		const momentTimestamp = new Date(timestamp);

		if (options.userTimeZone) {
			return formatTz(utcToZonedTime(momentTimestamp, options.userTimeZone), dateFormat);
		}

		return format(momentTimestamp, dateFormat);
	};
};

export default makeDateFormatter;
